import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import type { AuthContextValue, ProviderProps, CurrentUser } from 'src/types';
import { useTranslation } from 'react-i18next';
import { authService, usersService } from 'src/api/services';
import { setOnSessionExpired } from 'src/api/client';

const DEV_AUTO_LOGIN = import.meta.env.VITE_DEV_AUTO_LOGIN === 'true';
const DEV_EMAIL = import.meta.env.VITE_DEV_EMAIL as string | undefined;
const DEV_PASSWORD = import.meta.env.VITE_DEV_PASSWORD as string | undefined;

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export const AuthProvider: React.FC<ProviderProps> = ({ children }) => {
  const { i18n } = useTranslation();

  // Restore user from localStorage on mount using lazy initializer
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(() => {
    const user = localStorage.getItem('user');
    if (user) {
      try {
        return JSON.parse(user) as CurrentUser;
      } catch (error) {
        console.error('Failed to parse user from localStorage:', error);
        localStorage.removeItem('user');
        return null;
      }
    }
    return null;
  });

  // Dev auto-login: automatically log in when VITE_DEV_AUTO_LOGIN is set
  const autoLoginAttempted = useRef(false);
  useEffect(() => {
    if (!DEV_AUTO_LOGIN || !DEV_EMAIL || !DEV_PASSWORD || currentUser || autoLoginAttempted.current)
      return;
    autoLoginAttempted.current = true;
    authService
      .login(DEV_EMAIL, DEV_PASSWORD)
      .then((result) => {
        const user: CurrentUser = {
          email: result.email,
          token: result.token,
          is_admin: result.is_admin,
          userId: result.userId,
          name: result.name,
          preferred_language: result.preferred_language,
        };
        localStorage.setItem('authToken', result.token);
        if (result.refreshToken) localStorage.setItem('refreshToken', result.refreshToken);
        localStorage.setItem('user', JSON.stringify(user));
        setCurrentUser(user);
      })
      .catch((err) => console.error('Dev auto-login failed:', err));
  }, [currentUser]);

  // Apply the user's stored language to the UI whenever it's known — at login
  // and when a session is restored from localStorage on reload. Falls back to
  // i18n's own detection when logged out.
  useEffect(() => {
    if (currentUser?.preferred_language) {
      void i18n.changeLanguage(currentUser.preferred_language);
    }
  }, [currentUser?.preferred_language, i18n]);

  // Set when a refresh ultimately fails, so a Toast-aware component (App) can
  // show the "session expired" message. AuthProvider deliberately does NOT call
  // useToast itself (it lives above ToastProvider in the tree).
  const [sessionExpired, setSessionExpired] = useState(false);

  // The API client (outside React) signals here when a token refresh has
  // ultimately failed: tokens are already cleared, so reset React state and
  // raise the flag. App.tsx's currentUser gate then falls back to <Login/>.
  useEffect(() => {
    setOnSessionExpired(() => {
      setCurrentUser(null);
      setSessionExpired(true);
    });
    return () => setOnSessionExpired(null);
  }, []);

  const clearSessionExpired = (): void => setSessionExpired(false);

  const login = async (
    token: string,
    email: string,
    is_admin: boolean,
    userId: string,
    name: string,
    preferred_language: string,
    refreshToken?: string
  ): Promise<void> => {
    const user: CurrentUser = { email, token, is_admin, userId, name, preferred_language };
    localStorage.setItem('authToken', token);
    if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
    localStorage.setItem('user', JSON.stringify(user));
    setCurrentUser(user);
    // Note: Projects and installations will be fetched by their respective contexts
  };

  const logout = (): void => {
    // Best-effort server-side revoke of the refresh token, then clear locally.
    const refreshToken = localStorage.getItem('refreshToken');
    void authService.logout(refreshToken ?? undefined);
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    setCurrentUser(null);
    // Note: Projects and installations will be cleared by their respective contexts
  };

  // Backend bumps token_version + hands us a fresh token. Replace the
  // stored token in place so this session stays logged in; other open
  // sessions for the same user will get 401 on their next request.
  const changePassword = async (currentPassword: string, newPassword: string): Promise<void> => {
    const result = await authService.changePassword(currentPassword, newPassword);
    // The password change rotates both tokens (old refresh tokens are revoked
    // server-side); persist the fresh pair so this session keeps refreshing.
    localStorage.setItem('authToken', result.token);
    if (result.refresh_token) localStorage.setItem('refreshToken', result.refresh_token);
    setCurrentUser((prev) => {
      if (!prev) return prev;
      const updated: CurrentUser = { ...prev, token: result.token };
      localStorage.setItem('user', JSON.stringify(updated));
      return updated;
    });
  };

  // Persist the user's language choice to the backend and update the local
  // session. The useEffect above re-applies it to i18n. Throws on API failure
  // so callers can surface a toast; the UI language can be switched
  // independently (Navigation does so optimistically before calling this).
  const setPreferredLanguage = async (language: string): Promise<void> => {
    await usersService.updateMe({ preferred_language: language });
    setCurrentUser((prev) => {
      if (!prev) return prev;
      const updated: CurrentUser = { ...prev, preferred_language: language };
      localStorage.setItem('user', JSON.stringify(updated));
      return updated;
    });
  };

  const value: AuthContextValue = {
    currentUser,
    login,
    logout,
    changePassword,
    setPreferredLanguage,
    sessionExpired,
    clearSessionExpired,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
